import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("sn_user");
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Always refresh from /auth/me on mount when a token exists — the endpoint
    // returns server-computed fields (like trial_expired) that the /auth/login
    // response doesn't carry, so a stale localStorage snapshot would otherwise
    // hide the trial-expired banner until the user logged out and back in.
    const t = localStorage.getItem("sn_token");
    if (t) {
      api.get("/auth/me")
        .then((r) => {
          localStorage.setItem("sn_user", JSON.stringify(r.data));
          setUser(r.data);
        })
        .catch(() => {});
    }
  }, []);  // eslint-disable-line

  const login = async (email, password) => {
    setLoading(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      localStorage.setItem("sn_token", r.data.token);
      // Fetch /auth/me right after login so server-computed fields
      // (trial_expired, plan flags) land immediately — the login endpoint
      // itself doesn't carry them.
      try {
        const me = await api.get("/auth/me");
        localStorage.setItem("sn_user", JSON.stringify(me.data));
        setUser(me.data);
        return me.data;
      } catch {
        localStorage.setItem("sn_user", JSON.stringify(r.data.user));
        setUser(r.data.user);
        return r.data.user;
      }
    } finally { setLoading(false); }
  };

  const signup = async (payload) => {
    setLoading(true);
    try {
      const r = await api.post("/auth/signup", payload);
      localStorage.setItem("sn_token", r.data.token);
      localStorage.setItem("sn_user", JSON.stringify(r.data.user));
      setUser(r.data.user);
      return r.data.user;
    } finally { setLoading(false); }
  };

  const logout = () => {
    localStorage.removeItem("sn_token");
    localStorage.removeItem("sn_user");
    setUser(null);
  };

  // Store a fresh JWT (e.g. after a password reset) and hydrate the user via
  // /auth/me so downstream screens see the logged-in state immediately.
  const setSession = async ({ token }) => {
    if (!token) return null;
    localStorage.setItem("sn_token", token);
    try {
      const r = await api.get("/auth/me");
      localStorage.setItem("sn_user", JSON.stringify(r.data));
      setUser(r.data);
      return r.data;
    } catch {
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, setSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
